"use client";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { RefreshCw, X } from "lucide-react";
import currentRelease from "@/generated/deployment.json";
import { saveResumePoint } from "@/lib/scroll-resume";

type Release = { version: string; notes: string[] };
export function ServiceWorkerRegistrar() {
  const t = useTranslations("common");
  const pathname = usePathname();
  const [release, setRelease] = useState<Release | null>(null);
  const [open, setOpen] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  useEffect(() => {
    const onChange = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);
  useEffect(() => {
    let disposed = false;
    let checking = false;
    let announced = "";
    let request: AbortController | undefined;
    let registration: ServiceWorkerRegistration | undefined;
    if ('serviceWorker' in navigator) {
      void navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' }).then(reg => {
        if (!disposed) registration = reg;
      }).catch(() => {});
    }
    const check = async () => {
      if (checking || disposed || document.visibilityState === 'hidden') return;
      checking = true;
      request = new AbortController();
      const timeout = setTimeout(() => request?.abort(), 8000);
      try {
        const response = await fetch('/api/app-version', { cache: 'no-store', signal: request.signal });
        if (!response.ok) return;
        const next: unknown = await response.json();
        if (!next || typeof next !== 'object' || !('version' in next) || typeof next.version !== 'string' || !('notes' in next) || !Array.isArray(next.notes)) return;
        if (!disposed && next.version !== currentRelease.version && next.version !== announced) {
          announced = next.version;
          setRelease({ version: next.version, notes: next.notes.filter((note): note is string => typeof note === 'string').slice(0, 4) });
          setOpen(true);
          void registration?.update().catch(() => {});
        }
      } catch { /* Offline clients keep their current session. */ }
      finally { clearTimeout(timeout); checking = false; }
    };
    void check();
    const timer = setInterval(() => void check(), 60_000);
    const onVisible = () => void check();
    window.addEventListener('focus', onVisible);
    window.addEventListener('online', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      disposed = true;
      request?.abort();
      clearInterval(timer);
      window.removeEventListener('focus', onVisible);
      window.removeEventListener('online', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);
  // A slim, non-blocking pill above the player — not a modal (Gordon 2026-10-29:
  // "less pop ups"), and no release notes: they were raw commit messages.
  // On /launch it shows while preparing but never while presenting (full
  // screen), so a deploy can't interrupt the keynote — yet an open deck still
  // learns it is out of date.
  if (!open || !release || (pathname?.startsWith("/launch") && fullscreen)) return null;
  return (
    <div
      role="status"
      // One line, never wrapped (Gordon 2026-10-31): the pill takes the width it needs.
      className="fixed left-1/2 z-[110] flex w-max max-w-[calc(100vw_-_1rem)] -translate-x-1/2 items-center gap-1 whitespace-nowrap rounded-full border border-border bg-card/95 py-1 pl-4 pr-1 text-sm shadow-lg backdrop-blur bottom-[92px] sm:bottom-[calc(var(--player-height)_+_12px)]"
    >
      <span className="font-medium text-foreground">{t("appUpdated")}</span>
      <button
        type="button"
        onClick={() => { saveResumePoint(); window.location.reload(); }}
        className="ml-2 inline-flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-primary px-4 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
      >
        <RefreshCw className="h-3.5 w-3.5" aria-hidden />
        {t("refreshNow")}
      </button>
      <button
        type="button"
        onClick={() => setOpen(false)}
        aria-label={t("outdatedBrowserDismiss")}
        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <X className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}
